// Add or change a field: its name, type and the type's settings (choices, decimals, which table it links to,
// what a lookup or rollup reads, a formula).
import { useEffect, useMemo, useState } from "react";
import type { Choice, Field, FieldOptions, FieldType, FormulaHelp, TableData, TableListItem } from "./pApi";
import { pApi } from "./pApi";
import { CHOICE_TONES, EDITABLE, TYPE_INFO } from "./format";

const ORDER: FieldType[] = ["text", "longtext", "number", "currency", "percent", "date", "checkbox", "select", "multiselect", "person", "approval", "url", "email", "link", "lookup", "rollup", "formula", "autonumber", "created", "updated"];
const COLORS = Object.keys(CHOICE_TONES);
const ROLLUPS = [["sum", "Sum"], ["avg", "Average"], ["min", "Smallest"], ["max", "Largest"], ["count", "Count of linked records"], ["counta", "Count of values"], ["concat", "List of values"], ["earliest", "Earliest date"], ["latest", "Latest date"]];

export function FieldDialog({ data, field, after, onDone, onClose }: { data: TableData; field?: Field | null; after?: string; onDone: () => void; onClose: () => void }) {
  const editing = !!field;
  const [name, setName] = useState(field?.name ?? "");
  const [type, setType] = useState<FieldType>(field?.type ?? "text");
  const [opts, setOpts] = useState<FieldOptions>(() => JSON.parse(JSON.stringify(field?.options ?? {})));
  const [desc, setDesc] = useState(field?.description ?? "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [tables, setTables] = useState<TableListItem[] | null>(null);
  const [targetFields, setTargetFields] = useState<Field[] | null>(null);
  const [help, setHelp] = useState<FormulaHelp[]>([]);
  useEffect(() => { if (type === "link" && !tables) pApi.tables().then(setTables).catch(() => setTables([])); if (type === "formula" && !help.length) pApi.templates().then((t) => setHelp(t.functions)).catch(() => {}); }, [type]); // eslint-disable-line react-hooks/exhaustive-deps
  // What a lookup or rollup can read: the fields of the table its link points at.
  const linkFields = data.fields.filter((f) => f.type === "link" && f.key !== field?.key);
  const link = linkFields.find((f) => f.key === opts.link);
  useEffect(() => {
    setTargetFields(null);
    if (!link?.options.table) return;
    if (link.options.table === data.table.id) { setTargetFields(data.fields); return; }
    pApi.table(link.options.table).then((t) => setTargetFields(t.fields)).catch(() => setTargetFields([]));
  }, [link?.options.table]); // eslint-disable-line react-hooks/exhaustive-deps

  const choices = opts.choices ?? [];
  const setChoices = (c: Choice[]) => setOpts((o) => ({ ...o, choices: c }));
  const save = async () => {
    setBusy(true); setErr(null);
    try {
      const o: FieldOptions = { ...opts };
      if (type === "select" || type === "multiselect") o.choices = choices.filter((c) => c.name.trim());
      if (editing) await pApi.updateField(field!.id, { name: name.trim(), type, options: o, description: desc });
      else await pApi.addField(data.table.id, { name: name.trim(), type, options: o, after, description: desc });
      onDone();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); setBusy(false); }
  };
  const typeChange = editing && type !== field!.type;
  const lossy = typeChange && EDITABLE(field!) && EDITABLE({ ...field!, type });

  return (
    <div className="pv-modal-wrap" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pv-modal" role="dialog" aria-label={editing ? `Change ${field!.name}` : "Add a field"}>
        <div className="pv-modal-h"><h2>{editing ? `Change “${field!.name}”` : "Add a field"}</h2><button className="icon-btn" aria-label="Close" onClick={onClose}>×</button></div>
        <div className="pv-modal-b">
          {err && <div className="msg error" role="alert">{err}</div>}
          <div className="field"><label htmlFor="fd-name">Name</label><input id="fd-name" autoFocus className="input" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && name.trim() && save()} placeholder="Due date, Owner, Qty…" /></div>
          <div className="field">
            <span className="label">Type</span>
            <div className="pv-types" role="radiogroup" aria-label="Field type">
              {ORDER.filter((t) => !editing || (field!.type === "link") === (t === "link")).map((t) => (
                <button key={t} role="radio" aria-checked={type === t} className={`pv-type ${type === t ? "on" : ""}`} onClick={() => setType(t)} title={TYPE_INFO[t].hint}>
                  <span className="pg-ti">{TYPE_INFO[t].icon}</span><span>{TYPE_INFO[t].label}</span>
                </button>
              ))}
            </div>
            <span className="hint">{TYPE_INFO[type].hint}{lossy ? ". Values that don't fit the new type will be cleared." : ""}</span>
          </div>

          {(type === "select" || type === "multiselect") && (
            <div className="field">
              <span className="label">Choices</span>
              <div className="pv-choices">
                {choices.map((c, i) => (
                  <div key={i} className="pv-choice">
                    <button className="pv-dot" style={{ background: CHOICE_TONES[c.color ?? "gray"]?.dot }} aria-label="Change color" title="Change color"
                      onClick={() => setChoices(choices.map((x, j) => (j === i ? { ...x, color: COLORS[(COLORS.indexOf(x.color ?? "gray") + 1) % COLORS.length] } : x)))} />
                    <input className="input" value={c.name} aria-label={`Choice ${i + 1}`} onChange={(e) => setChoices(choices.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                      onKeyDown={(e) => { if (e.key === "Enter") setChoices([...choices.slice(0, i + 1), { name: "", color: COLORS[(i + 1) % COLORS.length] }, ...choices.slice(i + 1)]); }} />
                    <button className="icon-btn" aria-label="Move up" disabled={!i} onClick={() => { const n = [...choices]; [n[i - 1], n[i]] = [n[i], n[i - 1]]; setChoices(n); }}>↑</button>
                    <button className="icon-btn" aria-label="Remove choice" onClick={() => setChoices(choices.filter((_, j) => j !== i))}>×</button>
                  </div>
                ))}
                <button className="btn" onClick={() => setChoices([...choices, { name: "", color: COLORS[choices.length % COLORS.length] }])}>+ Add a choice</button>
              </div>
              {editing && <span className="hint">Renaming a choice here doesn't change records that already have it.</span>}
            </div>
          )}
          {(type === "number" || type === "currency" || type === "percent") && (
            <div className="field" style={{ maxWidth: 200 }}><label htmlFor="fd-dec">Decimal places</label>
              <select id="fd-dec" className="select" value={opts.decimals ?? (type === "currency" ? 2 : type === "percent" ? 1 : 0)} onChange={(e) => setOpts({ ...opts, decimals: Number(e.target.value) })}>{[0, 1, 2, 3, 4].map((d) => <option key={d} value={d}>{d}</option>)}</select>
            </div>
          )}
          {type === "link" && !editing && (
            <>
              <div className="field"><label htmlFor="fd-tbl">Links to</label>
                <select id="fd-tbl" className="select" value={opts.table ?? ""} onChange={(e) => setOpts({ ...opts, table: e.target.value })}>
                  <option value="">Pick a table…</option>
                  <option value={data.table.id}>This table ({data.table.name}): for predecessors, parent records…</option>
                  {(tables ?? []).filter((t) => t.id !== data.table.id && t.canEdit).map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                </select>
                {opts.table && opts.table !== data.table.id && <span className="hint">The other table gets a matching field, so the link shows on both sides.</span>}
              </div>
              <label className="pv-cb"><input type="checkbox" checked={!!opts.single} onChange={(e) => setOpts({ ...opts, single: e.target.checked })} /> One record only</label>
            </>
          )}
          {(type === "lookup" || type === "rollup") && (
            <>
              {!linkFields.length ? <div className="msg warn">Add a “Link to another table” field first; a {type} reads from the records it links to.</div> : (
                <>
                  <div className="field"><label htmlFor="fd-link">Through the link</label>
                    <select id="fd-link" className="select" value={opts.link ?? ""} onChange={(e) => setOpts({ ...opts, link: e.target.value, field: undefined })}><option value="">Pick a link field…</option>{linkFields.map((f) => <option key={f.key} value={f.key}>{f.name} ({data.links[f.options.table!]?.name ?? "a table"})</option>)}</select>
                  </div>
                  {opts.link && (
                    <div className="field"><label htmlFor="fd-tf">Field of the linked records</label>
                      <select id="fd-tf" className="select" value={opts.field ?? ""} onChange={(e) => setOpts({ ...opts, field: e.target.value })} disabled={!targetFields}>
                        <option value="">{targetFields ? "Pick a field…" : "Loading…"}</option>{(targetFields ?? []).filter((f) => f.type !== "lookup").map((f) => <option key={f.key} value={f.key}>{f.name}</option>)}
                      </select>
                    </div>
                  )}
                  {type === "rollup" && (
                    <div className="field"><label htmlFor="fd-fn">Roll up as</label>
                      <select id="fd-fn" className="select" value={opts.fn ?? ""} onChange={(e) => setOpts({ ...opts, fn: e.target.value })}><option value="">Pick…</option>{ROLLUPS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
                    </div>
                  )}
                </>
              )}
            </>
          )}
          {type === "formula" && <FormulaInput value={opts.formula ?? ""} onChange={(v) => setOpts({ ...opts, formula: v })} fields={data.fields.filter((f) => f.key !== field?.key)} help={help} />}
          {type === "autonumber" && <div className="field" style={{ maxWidth: 220 }}><label htmlFor="fd-pre">Prefix</label><input id="fd-pre" className="input" value={opts.prefix ?? ""} placeholder="T-" onChange={(e) => setOpts({ ...opts, prefix: e.target.value })} /><span className="hint">Shows {(opts.prefix ?? "")}1, {(opts.prefix ?? "")}2…</span></div>}
          {type === "approval" && <p className="muted" style={{ marginTop: 0 }}>Each record can be sent to one person to approve. They see it in Productivity under Waiting on you, and approving or rejecting is recorded in its history. Automations can ask for approval when, say, an amount is over a limit.</p>}

          <div className="field"><label htmlFor="fd-desc">Description <span className="muted" style={{ fontWeight: 400 }}>(optional)</span></label><textarea id="fd-desc" className="textarea" rows={2} value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="What goes here, shown when people hover the field name" /></div>
        </div>
        <div className="pv-modal-f">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={busy || !name.trim()} onClick={save}>{busy ? "Saving…" : editing ? "Save" : "Add field"}</button>
        </div>
      </div>
    </div>
  );
}

function FormulaInput({ value, onChange, fields, help }: { value: string; onChange: (v: string) => void; fields: Field[]; help: FormulaHelp[] }) {
  const [el, setEl] = useState<HTMLTextAreaElement | null>(null);
  const insert = (s: string) => {
    if (!el) { onChange(value + s); return; }
    const a = el.selectionStart, b = el.selectionEnd;
    const next = value.slice(0, a) + s + value.slice(b);
    onChange(next);
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(a + s.length, a + s.length); });
  };
  const groups = useMemo(() => [...new Set(help.map((h) => h.group))], [help]);
  return (
    <div className="field">
      <label htmlFor="fd-formula">Formula</label>
      <textarea id="fd-formula" ref={setEl} className="textarea mono pv-formula" rows={3} value={value} onChange={(e) => onChange(e.target.value)} placeholder='[Qty] * [Unit cost]     If([Status] = "Done", 1, 0)     DateDiff("day", Today(), [Due])' spellCheck={false} />
      <div className="pv-fhelp">
        <div>
          <div className="pv-fhelp-h">Fields</div>
          <div className="pv-fchips">{fields.map((f) => <button key={f.key} className="pv-chip" onClick={() => insert(`[${f.name}]`)}>{f.name}</button>)}</div>
        </div>
        <div>
          <div className="pv-fhelp-h">Functions</div>
          {groups.map((g) => (
            <div key={g} className="pv-fgroup">
              {help.filter((h) => h.group === g).map((h) => <button key={h.name} className="pv-fn" onClick={() => insert(h.usage)} title={h.does}><span className="mono">{h.usage}</span><span className="muted">{h.does}</span></button>)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
